/**
 * Adaptive personality: winning bots lean aggressive, losing bots lean
 * defensive, within a small band.
 */

import type { PlayerState, GameState, BotPersonality } from '../../types';

/**
 * Compute a pressure shift in [-0.2, +0.2] based on game state.
 * Negative = losing (shift defensive), positive = winning (shift aggressive).
 */
export function computeAdaptivePressure(player: PlayerState, gameState: GameState): number {
  const healthFraction = player.health / 100;
  const planet = gameState.planets[player.planetIndex];
  const planetDamage = 1 - planet.mass / planet.initialMass;

  // Compute team health advantage: average alive team health vs average alive enemy health
  let teamHealthSum = 0;
  let teamAliveCount = 0;
  let enemyHealthSum = 0;
  let enemyAliveCount = 0;
  for (const p of gameState.players) {
    if (!p.alive) continue;
    if (p.teamIndex === player.teamIndex) {
      teamHealthSum += p.health;
      teamAliveCount++;
    } else {
      enemyHealthSum += p.health;
      enemyAliveCount++;
    }
  }
  const avgTeamHealth = teamAliveCount > 0 ? teamHealthSum / teamAliveCount : 0;
  const avgEnemyHealth = enemyAliveCount > 0 ? enemyHealthSum / enemyAliveCount : 0;
  // Normalize to [-1, +1]: positive when team is healthier
  const maxHealth = 100;
  const teamHealthAdvantage = (avgTeamHealth - avgEnemyHealth) / maxHealth;

  const rawPressure = 0.4 * teamHealthAdvantage + 0.3 * (healthFraction - 0.5) - 0.3 * planetDamage;
  return Math.max(-0.2, Math.min(0.2, rawPressure));
}

/**
 * Apply adaptive pressure to a personality, returning a new personality object.
 * Aggression and riskTolerance shift by pressureShift; mobility is unchanged.
 */
export function applyAdaptivePressure(personality: BotPersonality, pressureShift: number): BotPersonality {
  return {
    aggression: Math.max(0, Math.min(1, personality.aggression + pressureShift)),
    riskTolerance: Math.max(0, Math.min(1, personality.riskTolerance + pressureShift)),
    mobility: personality.mobility,
  };
}
